import { useState, useEffect, useCallback } from "react";
import api, { API_BASE_URL } from "../../../utils/api";

/**
 * Custom Hook: useWorkOrderDetails
 *
 * Handles fetching and managing all data related to a single work order.
 *
 * @param {string|number} workOrderId - The ID of the current work order
 * @param {string|number} currentUserId - The ID of the logged-in user
 */
const useWorkOrderDetails = (workOrderId, currentUserId) => {
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [member, setMember] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [priorityLevels, setPriorityLevels] = useState([]);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [workOrderSubtypes, setWorkOrderSubtypes] = useState([]);
  const [workOrderLocations, setWorkOrderLocations] = useState([]);
  const [comments, setComments] = useState([]);
  const [imageURL, setImageURL] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);


  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedSubtypeId, setSelectedSubtypeId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComments = async () => {
    try {
      const res = await api.get(`/getComments/${workOrderId}`);
      setComments(res.data);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
  };

  const fetchAttachedFiles = async () => {
    try {
      const res = await api.get(`/workorder-files/${workOrderId}`);
      setAttachedFiles(res.data);

      // Preview first image file (if any)
      const firstImageFile = res.data.find(file =>
        file.file_type && file.file_type.startsWith("image/")
      );
      if (firstImageFile) {
        const imageUrl = `${API_BASE_URL}/workorderimages/${encodeURIComponent(firstImageFile.file_url.split(/(\\|\/)/g).pop())}`;
        setImageURL(imageUrl);
      } else {
        setImageURL(null);
      }
    } catch (err) {
      console.error("Failed to fetch attached files:", err);
    }
  };

const fetchAvailableProperties = useCallback(async () => {
  try {
    const res = await api.get(`/membersfilter`);
    setAvailableProperties(res.data || []);
  } catch (err) {
    console.error("Failed to fetch available properties:", err);
  }
}, []);

const handlePropertyChange = useCallback(async (event) => {
  const newPropertyId = event.target.value;
  try {
    await api.put(`/workorders/${workOrderDetails.order_id}/property`, {
      property_id: newPropertyId,
    });
    setWorkOrderDetails(prev => ({ ...prev, property_id: newPropertyId }));
  } catch (error) {
    console.error("Failed to update property ID:", error);
  }
}, [workOrderDetails]);



  const fetchDetails = useCallback(async () => {
    try {
      const workOrderResp = await api.get(`/getWorkOrders/${workOrderId}`);
      const workOrderData = workOrderResp.data;

      const memberResp = await api.get(`/members/${workOrderData.property_id}`);
      const memberData = memberResp.data;

      const eventsResp = await api.get(`/getEvents?work_order_id=${workOrderId}`);
      const assignableUsersResp = await api.get(`/users-by-role`);
      const priorityResp = await api.get(`/priority-levels`);
      const usersResp = await api.get(`/users`);
      const statusesResp = await api.get(`/statuses/workorders`);
      const workOrderTypesResp = await api.get(`/workorder-types`);

      const subtypesResp = await api.get(
        `/workorder-subtypes?type_id=${workOrderData.workorder_type_id}`
      );
      const locationsResp = await api.get(
        `/workorder-locations?type_id=${workOrderData.workorder_type_id}`
      );

      setWorkOrderDetails(workOrderData);
      setMember(memberData);
      setEvents(eventsResp.data.map(event => ({
        ...event,
        start: new Date(event.start_time),
        end: new Date(event.end_time),
      })));
      setAssignableUsers(assignableUsersResp.data);
      setPriorityLevels(priorityResp.data);
      setUsers(usersResp.data);
      setStatuses(statusesResp.data);
      setWorkOrderTypes(workOrderTypesResp.data);
      setWorkOrderSubtypes(subtypesResp.data);
      setWorkOrderLocations(locationsResp.data);

      setSelectedAssignee(workOrderData.assign_to || null);
      setSelectedPriority(workOrderData.priority || "");
      setSelectedTypeId(workOrderData.workorder_type_id || "");
      setSelectedSubtypeId(workOrderData.workorder_subtype_id || "");
      setSelectedLocationId(workOrderData.location_id || "");
    } catch (error) {
      console.error("Failed to fetch work order details:", error);
      setError("Failed to fetch details.");
    } finally {
      setIsLoading(false);
    }
  }, [workOrderId]);

 useEffect(() => {
  fetchDetails();
  fetchComments();
  fetchAttachedFiles();
  fetchAvailableProperties();
}, [fetchDetails, fetchAvailableProperties]);


  // 🔧 Handlers
  const handleTypeChange = async (event) => {
    const newTypeId = event.target.value;
    try {
      const [subtypesRes, locationsRes] = await Promise.all([
        api.get(`/workorder-subtypes-by-type?type_id=${newTypeId}`),
        api.get(`/workorder-locations-by-type?type_id=${newTypeId}`),
      ]);

      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        workorder_type_id: newTypeId,
        status: workOrderDetails.status,
        assign_to: workOrderDetails.assign_to,
        priority: workOrderDetails.priority,
        workorder_subtype_id: null,
        location_id: null,
      });

      setWorkOrderSubtypes(subtypesRes.data);
      setWorkOrderLocations(locationsRes.data);
      setSelectedTypeId(newTypeId);
      setSelectedSubtypeId(null);
      setSelectedLocationId(null);
      setWorkOrderDetails(prev => ({
        ...prev,
        workorder_type_id: newTypeId,
        workorder_subtype_id: null,
        location_id: null,
      }));
    } catch (error) {
      console.error("Failed to update type:", error);
    }
  };

  const handleSubtypeChange = async (event) => {
    const subtypeId = event.target.value;
    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        workorder_subtype_id: subtypeId,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
      });
      setSelectedSubtypeId(subtypeId);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLocationChange = async (event) => {
    const locationId = event.target.value;
    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        location_id: locationId,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
      });
      setSelectedLocationId(locationId);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAssigneeChange = async (event) => {
    const newAssignee = event.target.value;
    try {
      await api.put(`/assignWorkOrder`, {
        order_id: workOrderDetails.order_id,
        assign_to: newAssignee,
      });
      setSelectedAssignee(newAssignee);
      setWorkOrderDetails(prev => ({ ...prev, assign_to: newAssignee }));
    } catch (error) {
      console.error("Error assigning user:", error);
    }
  };

  const handlePriorityChange = async (event) => {
    const newPriority = event.target.value;
    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        priority: newPriority,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
      });
      setSelectedPriority(newPriority);
      setWorkOrderDetails(prev => ({ ...prev, priority: newPriority }));
    } catch (error) {
      console.error("Failed to update priority:", error);
    }
  };

  const calculateDaysOpen = (createdAt) => {
    if (!createdAt) return "N/A";
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffMs = now - createdDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays === 0 ? "Open Today" : `Open for ${diffDays} day(s)`;
  };


// 🔁 Re-fetch member info whenever property_id changes
useEffect(() => {
  const refreshMemberInfo = async () => {
    if (!workOrderDetails?.property_id) return;

    try {
      const memberResp = await api.get(`/members/${workOrderDetails.property_id}`);
      setMember(memberResp.data);
      console.log("✅ Member info refreshed for property:", workOrderDetails.property_id);
    } catch (err) {
      console.error("❌ Failed to refresh member info:", err);
    }
  };

  refreshMemberInfo();
}, [workOrderDetails?.property_id]);



  return {
    workOrderDetails,
    setWorkOrderDetails,
    member,
    setMember, 
    events,
    users,
    statuses,
    assignableUsers,
    priorityLevels,
    workOrderTypes,
    workOrderSubtypes,
    workOrderLocations,
    selectedAssignee,
    selectedPriority,
    availableProperties,
    handlePropertyChange,
    selectedTypeId,
    selectedSubtypeId,
    selectedLocationId,
    isLoading,
    error,
    imageURL,
    attachedFiles, // ✅ Exposed
    fetchDetails,
    fetchComments,
    comments,
    setComments,
    setEvents,
    setUsers,
    handleTypeChange,
    handleSubtypeChange,
    handleLocationChange,
    handleAssigneeChange,
    handlePriorityChange,
    calculateDaysOpen,

  };
};

export default useWorkOrderDetails;
